import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { CHECK_IN } from '../graphql/hospedajes';
import { GET_RESERVA } from '../graphql/reservas';
import { GET_HABITACIONES_CON_OCUPACION } from '../graphql/habitaciones';
import { GET_HUESPED_POR_DOCUMENTO } from '../graphql/huespedes';
import BuscadorClienteHuesped from '../components/hospedajes/BuscadorClienteHuesped';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import Select from '../components/shared/Select';
import SuccessModal from '../components/shared/SuccessModal';
import { Search, UserPlus, Home, Calendar, Users, Plus, X, Award, Bed } from 'lucide-react';
import './CheckIn.css';

function CheckIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reservaIdParam = searchParams.get('reserva');

  const [modo, setModo] = useState(reservaIdParam ? 'reserva' : 'walkin');
  const [busquedaReserva, setBusquedaReserva] = useState('');
  const [reservaSeleccionada, setReservaSeleccionada] = useState(null);
  const [ocupantes, setOcupantes] = useState([]);

  const [formData, setFormData] = useState({
    habitacion_id: '',
    huesped_id: null,
    cliente_id: null,
    reserva_id: null,
    fecha_entrada: new Date().toISOString().slice(0, 10),
    fecha_salida: '',
    num_adultos: 1,
    num_ninos: 0,
    observaciones: ''
  });

  const [huespedConfirmado, setHuespedConfirmado] = useState(null);
  const [successModal, setSuccessModal] = useState({ isOpen: false, message: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });

  // Queries
  const { data: habitacionesData } = useQuery(GET_HABITACIONES_CON_OCUPACION);

  const [buscarReserva, { data: reservaData, loading: buscandoReserva }] = useLazyQuery(GET_RESERVA);
  const [buscarHuespedPorDoc] = useLazyQuery(GET_HUESPED_POR_DOCUMENTO);

  // Mutations
  const [checkIn, { loading: procesandoCheckIn }] = useMutation(CHECK_IN, {
    onCompleted: (data) => {
      setSuccessModal({ isOpen: true, message: `Check-In realizado exitosamente. Hospedaje: ${data.checkIn.codigo}` });
    },
    onError: (error) => {
      setErrorModal({ isOpen: true, message: `Error al realizar check-in: ${error.message}` });
    }
  });

  // Mostrar cualquier habitación activa con camas disponibles (walk-in).
  // Se excluye solo mantenimiento porque la habitación está fuera de servicio.
  const habitaciones = (habitacionesData?.habitacionesConOcupacion || []).filter(
    h => h.activa && h.estado !== 'mantenimiento' && h.camas_disponibles > 0
  );

  // Obtener la habitación seleccionada para conocer su capacidad
  const habitacionSeleccionada = habitaciones.find(
    h => h.id.toString() === formData.habitacion_id
  );
  const capacidadCamas = habitacionSeleccionada?.camas_disponibles || habitacionSeleccionada?.capacidad || 0;

  // Si viene con reservaId en URL, buscarla automáticamente
  useEffect(() => {
    if (reservaIdParam) {
      buscarReserva({ variables: { id: parseInt(reservaIdParam) } });
    }
  }, [reservaIdParam]);

  // Cuando se encuentra una reserva, pre-llenar el formulario
  useEffect(() => {
    if (reservaData?.reserva) {
      const reserva = reservaData.reserva;
      setReservaSeleccionada(reserva);
      setFormData(prev => ({
        ...prev,
        habitacion_id: reserva.habitacion_id.toString(),
        reserva_id: reserva.id,
        fecha_entrada: reserva.fecha_entrada.split('T')[0],
        fecha_salida: reserva.fecha_salida.split('T')[0],
        num_adultos: reserva.num_adultos || 1,
        num_ninos: reserva.num_ninos || 0,
        huesped_id: reserva.huesped_id
      }));
      const huesped = reserva.huesped;
      setHuespedConfirmado({
        id: reserva.huesped_id,
        cliente_id: huesped?.cliente_id || reserva.cliente_id
      });

      // Auto-crear ocupante titular desde los datos de la reserva
      if (huesped) {
        const precioMensualGrado = parseFloat(huesped.grado_militar?.precio_noche) || 0;
        setOcupantes([{
          huesped_id: huesped.id,
          nombre: huesped.nombre_completo || `${huesped.nombre || ''} ${huesped.apellido || ''}`.trim(),
          numero_documento: huesped.numero_documento || '',
          tipo_documento: huesped.tipo_documento || 'CC',
          grado_militar_id: huesped.grado_militar_id || null,
          grado_nombre: huesped.grado_militar?.nombre || '',
          grado_militar: huesped.grado_militar || null,
          precio_noche: precioMensualGrado / 30,
          precio_mensual: precioMensualGrado,
          tipo_tarifa: 'mensual',
          cama_numero: 1,
          es_titular: true
        }]);
      }
    }
  }, [reservaData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Si cambia la habitación, limpiar ocupantes que excedan la nueva capacidad
    if (name === 'habitacion_id') {
      const hab = habitaciones.find(h => h.id.toString() === value);
      if (hab && ocupantes.length > hab.capacidad) {
        setOcupantes(ocupantes.slice(0, hab.capacidad));
      }
    }
  };

  const handleBuscarReserva = () => {
    if (!busquedaReserva) {
      setErrorModal({ isOpen: true, message: 'Ingresa el ID de la reserva' });
      return;
    }
    buscarReserva({ variables: { id: parseInt(busquedaReserva) } });
  };

  const handleClienteSeleccionado = (cliente) => {
    setFormData(prev => ({
      ...prev,
      cliente_id: cliente.id
    }));
  };

  const handleHuespedConfirmado = (huesped) => {
    setHuespedConfirmado(huesped);
    setFormData(prev => ({
      ...prev,
      huesped_id: huesped.id,
      cliente_id: huesped.cliente_id
    }));

    // Auto-crear primer ocupante (titular) si no existe
    if (ocupantes.length === 0 || !ocupantes.find(o => o.huesped_id === huesped.id)) {
      const precioMensualGrado = parseFloat(huesped.grado_militar?.precio_noche) || 0;
      const nuevoOcupante = {
        huesped_id: huesped.id,
        nombre: `${huesped.nombre || ''} ${huesped.apellido || ''}`.trim(),
        numero_documento: huesped.numero_documento || '',
        tipo_documento: huesped.tipo_documento || 'CC',
        grado_militar_id: huesped.grado_militar_id || null,
        grado_nombre: huesped.grado_militar?.nombre || '',
        grado_militar: huesped.grado_militar || null,
        precio_noche: precioMensualGrado / 30,
        precio_mensual: precioMensualGrado,
        tipo_tarifa: 'mensual',
        cama_numero: 1,
        es_titular: true
      };
      // Reemplazar el primer ocupante si existe, o agregar
      setOcupantes(prev => {
        const sinTitular = prev.filter(o => !o.es_titular);
        return [nuevoOcupante, ...sinTitular];
      });
    }
  };

  // Agregar nueva cama/ocupante
  const agregarOcupante = () => {
    if (ocupantes.length >= capacidadCamas) {
      setErrorModal({
        isOpen: true,
        message: `La habitación solo tiene ${capacidadCamas} cama(s). No se pueden agregar más ocupantes.`
      });
      return;
    }
    const nuevaCama = ocupantes.length + 1;
    setOcupantes([...ocupantes, {
      huesped_id: null,
      nombre: '',
      numero_documento: '',
      tipo_documento: 'CC',
      grado_militar_id: null,
      grado_nombre: '',
      grado_militar: null,
      precio_noche: 0,
      precio_mensual: 0,
      tipo_tarifa: 'mensual',
      cama_numero: nuevaCama,
      es_titular: false
    }]);
  };

  const eliminarOcupante = (index) => {
    // No permitir eliminar al titular
    if (ocupantes[index]?.es_titular) {
      setErrorModal({ isOpen: true, message: 'No se puede eliminar al huésped titular' });
      return;
    }
    const nuevos = ocupantes.filter((_, i) => i !== index);
    // Re-numerar camas
    setOcupantes(nuevos.map((oc, i) => ({ ...oc, cama_numero: i + 1 })));
  };

  const actualizarOcupante = (index, campo, valor) => {
    const nuevos = [...ocupantes];
    nuevos[index] = { ...nuevos[index], [campo]: valor };

    // Si cambia tipo_tarifa, recalcular precios según grado
    if (campo === 'tipo_tarifa') {
      const grado = nuevos[index].grado_militar;
      if (valor === 'temporal') {
        const temporal = parseFloat(grado?.precio_noche_temporal) || 0;
        const mensualFallback = parseFloat(grado?.precio_noche) || 0;
        nuevos[index].precio_noche = temporal > 0 ? temporal : (mensualFallback / 30);
        nuevos[index].precio_mensual = 0;
      } else {
        const mensual = parseFloat(grado?.precio_noche) || 0;
        nuevos[index].precio_mensual = mensual;
        nuevos[index].precio_noche = mensual / 30;
      }
    }

    // Si el usuario edita manualmente el precio_noche en modo temporal, sólo ese campo cambia
    // Si edita en modo mensual, se interpreta como precio_mensual y se recalcula precio_noche
    if (campo === 'precio_mensual') {
      nuevos[index].precio_noche = (parseFloat(valor) || 0) / 30;
    }

    setOcupantes(nuevos);
  };

  // Buscar soldado por documento para autocompletar ocupante
  const buscarOcupantePorDocumento = async (index) => {
    const oc = ocupantes[index];
    if (!oc.numero_documento || oc.numero_documento.trim().length < 3) return;

    try {
      const { data } = await buscarHuespedPorDoc({
        variables: { numero_documento: oc.numero_documento.trim() }
      });

      if (data?.huespedPorDocumento) {
        const h = data.huespedPorDocumento;
        const tipoTarifa = ocupantes[index].tipo_tarifa || 'mensual';
        const mensualGrado = parseFloat(h.grado_militar?.precio_noche) || 0;
        const temporalGrado = parseFloat(h.grado_militar?.precio_noche_temporal) || 0;
        const precioNoche = tipoTarifa === 'temporal'
          ? (temporalGrado > 0 ? temporalGrado : mensualGrado / 30)
          : mensualGrado / 30;
        const precioMensual = tipoTarifa === 'mensual' ? mensualGrado : 0;
        const nuevos = [...ocupantes];
        nuevos[index] = {
          ...nuevos[index],
          huesped_id: h.id,
          tipo_documento: h.tipo_documento || oc.tipo_documento,
          nombre: `${h.nombre || ''} ${h.apellido || ''}`.trim(),
          grado_militar_id: h.grado_militar_id || null,
          grado_nombre: h.grado_militar?.nombre || '',
          grado_militar: h.grado_militar || null,
          precio_noche: precioNoche,
          precio_mensual: precioMensual,
        };
        setOcupantes(nuevos);
      }
    } catch (error) {
      console.log('Ocupante no encontrado en registros:', oc.numero_documento);
    }
  };

  // Calcular total según tarifa del ocupante
  // - mensual: precio_mensual (total del mes)
  // - temporal: precio_noche (por noche)
  const totalMensual = ocupantes
    .filter(oc => (oc.tipo_tarifa || 'mensual') === 'mensual')
    .reduce((sum, oc) => sum + (parseFloat(oc.precio_mensual) || 0), 0);
  const totalTemporalPorNoche = ocupantes
    .filter(oc => oc.tipo_tarifa === 'temporal')
    .reduce((sum, oc) => sum + (parseFloat(oc.precio_noche) || 0), 0);
  const hayMensual = ocupantes.some(oc => (oc.tipo_tarifa || 'mensual') === 'mensual');
  const hayTemporal = ocupantes.some(oc => oc.tipo_tarifa === 'temporal');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.habitacion_id) {
      setErrorModal({ isOpen: true, message: 'Selecciona una habitación' });
      return;
    }

    if (!formData.fecha_entrada || !formData.fecha_salida) {
      setErrorModal({ isOpen: true, message: 'Completa las fechas de entrada y salida' });
      return;
    }

    if (!formData.huesped_id) {
      setErrorModal({ isOpen: true, message: 'Debes confirmar los datos del cliente y huésped' });
      return;
    }

    // Validar que los acompañantes agregados tengan nombre
    const acompanantesInvalidos = ocupantes.filter(oc => !oc.es_titular && (!oc.nombre || oc.nombre.trim() === ''));
    if (acompanantesInvalidos.length > 0) {
      setErrorModal({ isOpen: true, message: 'Todos los acompañantes deben tener nombre' });
      return;
    }

    // Preparar ocupantes para enviar
    const ocupantesData = ocupantes.map(oc => ({
      huesped_id: oc.huesped_id ? parseInt(oc.huesped_id) : null,
      nombre: oc.nombre,
      numero_documento: oc.numero_documento || null,
      tipo_documento: oc.tipo_documento || 'CC',
      grado_militar_id: oc.grado_militar_id ? parseInt(oc.grado_militar_id) : null,
      precio_noche: parseFloat(oc.precio_noche) || 0,
      precio_mensual: parseFloat(oc.precio_mensual) || 0,
      tipo_tarifa: oc.tipo_tarifa || 'mensual',
      cama_numero: oc.cama_numero,
    }));

    try {
      await checkIn({
        variables: {
          input: {
            habitacion_id: parseInt(formData.habitacion_id),
            huesped_id: parseInt(formData.huesped_id),
            reserva_id: formData.reserva_id ? parseInt(formData.reserva_id) : null,
            fecha_entrada: formData.fecha_entrada,
            fecha_salida_prevista: formData.fecha_salida,
            num_adultos: ocupantes.length,
            num_ninos: 0,
            ocupantes: ocupantesData,
            observaciones: formData.observaciones || null
          }
        }
      });
    } catch (error) {
      console.error('Error en check-in:', error);
    }
  };

  return (
    <div className="checkin-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Check-In</h1>
          <p className="page-subtitle">Registrar entrada de soldados por cama</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/hospedajes')}>
          Volver a Hospedajes
        </Button>
      </div>

      {/* Selector de Modo */}
      <div className="modo-selector">
        <Button
          variant={modo === 'reserva' ? 'primary' : 'outline'}
          onClick={() => setModo('reserva')}
          icon={<Calendar size={18} />}
        >
          Desde Reserva
        </Button>
        <Button
          variant={modo === 'walkin' ? 'primary' : 'outline'}
          onClick={() => setModo('walkin')}
          icon={<UserPlus size={18} />}
        >
          Walk-In (Sin Reserva)
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="checkin-form">
        {/* Búsqueda de Reserva */}
        {modo === 'reserva' && !reservaSeleccionada && (
          <div className="checkin-seccion">
            <h3>Buscar Reserva</h3>
            <div className="busqueda-group">
              <Input
                type="number"
                value={busquedaReserva}
                onChange={(e) => setBusquedaReserva(e.target.value)}
                placeholder="ID de Reserva"
              />
              <Button
                type="button"
                onClick={handleBuscarReserva}
                disabled={buscandoReserva}
                icon={<Search size={18} />}
              >
                {buscandoReserva ? 'Buscando...' : 'Buscar'}
              </Button>
            </div>
          </div>
        )}

        {/* Información de Reserva (si existe) */}
        {reservaSeleccionada && (
          <div className="reserva-info">
            <h4>Reserva: {reservaSeleccionada.codigo}</h4>
            <div className="info-grid">
              <div>
                <span className="label">Huésped:</span>
                <span className="valor">{reservaSeleccionada.huesped?.nombre_completo}</span>
              </div>
              <div>
                <span className="label">Habitación:</span>
                <span className="valor">
                  {reservaSeleccionada.habitacion?.numero} - {reservaSeleccionada.habitacion?.tipo}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Selección de Habitación (Walk-in) */}
        {modo === 'walkin' && (
          <div className="checkin-seccion">
            <h3><Home size={20} /> Habitación</h3>
            <Select
              label="Habitación Disponible *"
              name="habitacion_id"
              value={formData.habitacion_id}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Seleccionar habitación...' },
                ...habitaciones.map(hab => ({
                  value: hab.id.toString(),
                  label: `${hab.numero} - ${hab.tipo} | ${hab.camas_disponibles ?? hab.capacidad}/${hab.capacidad} cama(s) disponibles`
                }))
              ]}
              required
            />
            {habitacionSeleccionada && (
              <div className="info-camas" style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#4a5568', fontSize: '0.9rem' }}>
                <Bed size={16} />
                <span>
                  <strong>{habitacionSeleccionada.camas_disponibles ?? habitacionSeleccionada.capacidad}</strong> de {habitacionSeleccionada.capacidad} cama(s) disponibles
                </span>
              </div>
            )}
          </div>
        )}

        {/* Búsqueda/Creación de Cliente y Huésped (Walk-in) */}
        {modo === 'walkin' && (
          <div className="checkin-seccion">
            <BuscadorClienteHuesped
              onClienteSeleccionado={handleClienteSeleccionado}
              onHuespedConfirmado={handleHuespedConfirmado}
              disabled={procesandoCheckIn}
            />
          </div>
        )}

        {/* Fechas */}
        <div className="checkin-seccion">
          <h3><Calendar size={20} /> Fechas de Hospedaje</h3>
          <div className="form-row">
            <Input
              type="date"
              label="Fecha Entrada *"
              name="fecha_entrada"
              value={formData.fecha_entrada}
              onChange={handleInputChange}
              required
              disabled={modo === 'reserva'}
            />
            <Input
              type="date"
              label="Fecha Salida *"
              name="fecha_salida"
              value={formData.fecha_salida}
              onChange={handleInputChange}
              required
              disabled={modo === 'reserva'}
            />
          </div>
        </div>

        {/* Ocupantes por Cama */}
        <div className="checkin-seccion">
          <div className="seccion-header">
            <h3><Users size={20} /> Ocupantes por Cama</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {capacidadCamas > 0 && (
                <span style={{ fontSize: '0.85rem', color: '#718096' }}>
                  {ocupantes.length} / {capacidadCamas} camas
                </span>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={agregarOcupante}
                icon={<Plus size={16} />}
                disabled={!formData.habitacion_id || ocupantes.length >= capacidadCamas}
              >
                Agregar Acompañante
              </Button>
            </div>
          </div>

          {ocupantes.length === 0 ? (
            <p className="texto-vacio">
              Selecciona un huésped titular para configurar la tarifa y agregar acompañantes.
            </p>
          ) : (
            <div className="acompanantes-lista">
              {ocupantes.map((oc, index) => (
                <div key={index} className="acompanante-card" style={oc.es_titular ? { borderLeft: '4px solid #3182ce' } : {}}>
                  <div className="acompanante-card__header">
                    <span className="acompanante-numero" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Bed size={16} />
                      Cama #{oc.cama_numero}
                      {oc.es_titular && (
                        <span style={{
                          background: '#3182ce', color: 'white', padding: '2px 8px',
                          borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600
                        }}>
                          TITULAR
                        </span>
                      )}
                    </span>
                    {!oc.es_titular && (
                      <button
                        type="button"
                        className="btn-eliminar"
                        onClick={() => eliminarOcupante(index)}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  <div className="acompanante-card__row">
                    <Select
                      label="Tipo Doc."
                      value={oc.tipo_documento}
                      onChange={(e) => actualizarOcupante(index, 'tipo_documento', e.target.value)}
                      options={[
                        { value: 'CC', label: 'CC - Cédula' },
                        { value: 'CE', label: 'CE - Extranjería' },
                        { value: 'TI', label: 'TI - Tarjeta' },
                        { value: 'Pasaporte', label: 'Pasaporte' },
                      ]}
                      disabled={oc.es_titular}
                    />
                    <Input
                      label="Nro. Documento"
                      placeholder={oc.es_titular ? '' : 'Ingresa y presiona Tab'}
                      value={oc.numero_documento}
                      onChange={(e) => actualizarOcupante(index, 'numero_documento', e.target.value)}
                      onBlur={() => !oc.es_titular && buscarOcupantePorDocumento(index)}
                      disabled={oc.es_titular}
                    />
                    <Input
                      label="Nombre *"
                      placeholder="Nombre completo"
                      value={oc.nombre}
                      onChange={(e) => actualizarOcupante(index, 'nombre', e.target.value)}
                      disabled={oc.es_titular}
                    />
                  </div>

                  <div className="acompanante-card__row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                      <Award size={16} style={{ color: '#805AD5', flexShrink: 0 }} />
                      <Input
                        label="Grado Militar"
                        value={oc.grado_nombre || 'Sin grado'}
                        disabled
                        style={{ flex: 1 }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.3rem', color: 'var(--hotel-text-secondary, #718096)' }}>Tipo Tarifa</label>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                          <input
                            type="radio"
                            name={`tipo_tarifa_${index}`}
                            value="mensual"
                            checked={oc.tipo_tarifa === 'mensual'}
                            onChange={() => actualizarOcupante(index, 'tipo_tarifa', 'mensual')}
                          />
                          Mensual
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                          <input
                            type="radio"
                            name={`tipo_tarifa_${index}`}
                            value="temporal"
                            checked={oc.tipo_tarifa === 'temporal'}
                            onChange={() => actualizarOcupante(index, 'tipo_tarifa', 'temporal')}
                          />
                          Temporal
                        </label>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      {(oc.tipo_tarifa || 'mensual') === 'mensual' ? (
                        <Input
                          label="Precio/Mes"
                          type="number"
                          value={oc.precio_mensual}
                          onChange={(e) => actualizarOcupante(index, 'precio_mensual', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="any"
                        />
                      ) : (
                        <Input
                          label="Precio/Noche"
                          type="number"
                          value={oc.precio_noche}
                          onChange={(e) => actualizarOcupante(index, 'precio_noche', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="any"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Resumen de precio total: por mes (mensual) y/o por noche (temporal) */}
              {ocupantes.length > 0 && (
                <div style={{
                  background: '#EBF8FF', border: '1px solid #BEE3F8',
                  borderRadius: '8px', padding: '1rem', marginTop: '0.5rem'
                }}>
                  {hayMensual && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, color: '#2D3748' }}>
                        Total por mes (tarifa mensual)
                      </span>
                      <span style={{ fontWeight: 700, fontSize: '1.2rem', color: '#2B6CB0' }}>
                        ${totalMensual.toLocaleString('es-CO')}
                      </span>
                    </div>
                  )}
                  {hayTemporal && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: hayMensual ? '0.5rem' : 0 }}>
                      <span style={{ fontWeight: 600, color: '#2D3748' }}>
                        Total por noche (tarifa temporal)
                      </span>
                      <span style={{ fontWeight: 700, fontSize: '1.2rem', color: '#2B6CB0' }}>
                        ${totalTemporalPorNoche.toLocaleString('es-CO')}
                      </span>
                    </div>
                  )}
                  {hayMensual && (
                    <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.4rem', fontStyle: 'italic' }}>
                      * La tarifa mensual se cobra por bloques de 30 días (1-30 noches = 1 mes completo, 31-60 = 2 meses, etc.)
                    </div>
                  )}
                  {ocupantes.length > 1 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#4A5568', borderTop: '1px solid #BEE3F8', paddingTop: '0.5rem' }}>
                      {ocupantes.map((oc, i) => {
                        const esMensual = (oc.tipo_tarifa || 'mensual') === 'mensual';
                        const monto = esMensual ? (parseFloat(oc.precio_mensual) || 0) : (parseFloat(oc.precio_noche) || 0);
                        return (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Cama {oc.cama_numero}: {oc.nombre || '(sin nombre)'} <em style={{ color: '#718096' }}>({esMensual ? 'mensual' : 'temporal'})</em></span>
                            <span>${monto.toLocaleString('es-CO')}{esMensual ? '/mes' : '/noche'}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Observaciones */}
        <div className="checkin-seccion">
          <label htmlFor="observaciones">Observaciones</label>
          <textarea
            id="observaciones"
            name="observaciones"
            value={formData.observaciones}
            onChange={handleInputChange}
            rows={3}
            className="textarea-observaciones"
            placeholder="Notas adicionales sobre el check-in..."
          />
        </div>

        {/* Acciones */}
        <div className="checkin-acciones">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/hospedajes')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={procesandoCheckIn}
            loading={procesandoCheckIn}
          >
            {procesandoCheckIn ? 'Procesando...' : 'Realizar Check-In'}
          </Button>
        </div>
      </form>

      {/* Modales de éxito y error */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => {
          const esCheckInExitoso = successModal.message.includes('Check-In realizado');
          setSuccessModal({ isOpen: false, message: '' });
          if (esCheckInExitoso) {
            navigate('/hospedajes');
          }
        }}
        type="success"
        message={successModal.message}
      />

      <SuccessModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        type="error"
        message={errorModal.message}
      />
    </div>
  );
}

export default CheckIn;
